import NotFound from "components/NotFoundPage";
import ErrorComponent from "components/ErrorPage";
import { CombinedError } from "urql";
import { withData, UrqlPageType } from "utils/urql";
import { ArticleRenderNotSupported } from "utils/error";

interface ErrorProps {
  statusCode: number;
}

interface Headers {
  [key: string]: string;
}

const ErrorPage: UrqlPageType<ErrorProps> = ({ statusCode }) => {
  if (statusCode === 404) {
    return <NotFound />;
  }

  return <ErrorComponent />;
};

ErrorPage.getInitialProps = async ({ res, req, err, asPath }) => {
  const statusCode = err?.statusCode || res?.statusCode || 500;
  const props = { statusCode };

  if (err instanceof ArticleRenderNotSupported && res) {
    res.statusCode = err.statusCode;
    res.statusMessage = err.statusMessage;
    return props;
  }

  // Do not record an exception in Sentry for 404
  if (statusCode === 404) {
    if (res) {
      res.statusCode = statusCode;
    }
    return props;
  }

  // Use dynamic import of sentry so that we can use Sentry.Handlers, which is
  // available in @sentry/node but not @sentry/browser. Our webpack config has
  // a resolve alias of @sentry/node to @sentry/browser for the client build
  // so we get typescript errors with a static import at the top of the file.
  // By putting the import inside getInitialProps we guarantee that the import
  // will only be evaluated in the SSR build.
  const Sentry = await import("@sentry/node");
  Sentry.withScope((scope) => {
    if (!err) {
      // If this point is reached, getInitialProps was called without any
      // information about what the error might be. This is unexpected and may
      // indicate a bug introduced in Next.js, so record it in Sentry
      err = new Error(
        `_error.js getInitialProps missing data at path: ${asPath}`
      );
    }

    if (req) {
      scope.addEventProcessor((event) => {
        const headers: Headers = {};
        Object.entries(req.headers).forEach(([k, v]) => {
          headers[k] = Array.isArray(v) ? v.join(", ") : v || "";
        });
        event.request = Sentry.Handlers.extractRequestData(req);
        let ip;
        if (headers["x-forwarded-for"]) {
          ip = headers["x-forwarded-for"].split(",")[0];
        } else {
          ip = headers["x-real-ip"] || req?.connection?.remoteAddress;
        }
        if (ip) {
          event.user = {
            ...event.user,
            ip_address: ip,
          };
        }
        return event;
      });
    }
    if (err instanceof CombinedError) {
      err.graphQLErrors.forEach((e) => Sentry.captureException(e));
    } else {
      Sentry.captureException(err);
    }
  });

  return props;
};

export default withData(ErrorPage);
